import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendDeleteRequest, sendGetRequest, sendPatchRequest, sendPostRequest } from '../../../api/requestUtil';
import {
  ContentSearchResult,
  RecommendationItem,
  RecommendationsAdminState,
} from '../types/recommendations.types';

export const fetchRecommendations = createAsyncThunk<
  RecommendationItem[],
  { sourceType: string; sourceId: number }
>('recommendations/fetchPublic', async ({ sourceType, sourceId }) => {
  return sendGetRequest(`/api/recommendations/${sourceType}/${sourceId}/`);
});

export const fetchAdminState = createAsyncThunk<
  RecommendationsAdminState,
  { sourceType: string; sourceId: number; bust?: boolean },
  { state: AppState }
>('recommendations/fetchAdmin', async ({ sourceType, sourceId, bust = false }, { getState }) => {
  const token = getState().user.authToken;
  const qs = bust ? '?bust=true' : '';
  return sendGetRequest(`/api/recommendations/${sourceType}/${sourceId}/admin/${qs}`, token);
});

export const addPinnedItem = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; contentType: string; contentId: number; slotPosition: number },
  { state: AppState }
>('recommendations/addPinned', async ({ sourceType, sourceId, contentType, contentId, slotPosition }, { getState }) => {
  const token = getState().user.authToken;
  await sendPostRequest(`/api/recommendations/${sourceType}/${sourceId}/pinned/`, {
    content_type: contentType,
    content_id: contentId,
    slot_position: slotPosition,
  }, token);
});

export const removePinnedItem = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; recId: number },
  { state: AppState }
>('recommendations/removePinned', async ({ sourceType, sourceId, recId }, { getState }) => {
  const token = getState().user.authToken;
  await sendDeleteRequest(`/api/recommendations/${sourceType}/${sourceId}/pinned/${recId}/`, token);
});

export const reorderPinnedItems = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; order: number[] },
  { state: AppState }
>('recommendations/reorderPinned', async ({ sourceType, sourceId, order }, { getState }) => {
  const token = getState().user.authToken;
  await sendPatchRequest(`/api/recommendations/${sourceType}/${sourceId}/pinned/reorder/`, { order }, token);
});

export const addExcludedItem = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; contentType: string; contentId: number; mutualExclude: boolean },
  { state: AppState }
>('recommendations/addExcluded', async ({ sourceType, sourceId, contentType, contentId, mutualExclude }, { getState }) => {
  const token = getState().user.authToken;
  await sendPostRequest(`/api/recommendations/${sourceType}/${sourceId}/excluded/`, {
    content_type: contentType,
    content_id: contentId,
    mutual_exclude: mutualExclude,
  }, token);
});

export const removeExcludedItem = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; recId: number },
  { state: AppState }
>('recommendations/removeExcluded', async ({ sourceType, sourceId, recId }, { getState }) => {
  const token = getState().user.authToken;
  await sendDeleteRequest(`/api/recommendations/${sourceType}/${sourceId}/excluded/${recId}/`, token);
});

export const updateMutualExclude = createAsyncThunk<
  void,
  { sourceType: string; sourceId: number; recId: number; mutualExclude: boolean },
  { state: AppState }
>('recommendations/updateMutualExclude', async ({ sourceType, sourceId, recId, mutualExclude }, { getState }) => {
  const token = getState().user.authToken;
  await sendPatchRequest(`/api/recommendations/${sourceType}/${sourceId}/excluded/${recId}/`, {
    mutual_exclude: mutualExclude,
  }, token);
});

export const generateRecommendations = createAsyncThunk<
  RecommendationsAdminState,
  { sourceType: string; sourceId: number },
  { state: AppState }
>('recommendations/generate', async ({ sourceType, sourceId }, { getState }) => {
  const token = getState().user.authToken;
  return sendPostRequest(`/api/recommendations/${sourceType}/${sourceId}/generate/`, {}, token);
});

export const searchContent = createAsyncThunk<
  ContentSearchResult[],
  { q: string },
  { state: AppState }
>('recommendations/search', async ({ q }, { getState }) => {
  const token = getState().user.authToken;
  return sendGetRequest(`/api/recommendations/search/?q=${encodeURIComponent(q)}`, token);
});

export interface UrlLookupResult {
  found: boolean;
  content_type: string | null;
  content_id: number | null;
  title: string | null;
  description: string | null;
  image: string | null;
  meta_label: string | null;
}

export const lookupContentByUrl = createAsyncThunk<
  UrlLookupResult,
  { url: string },
  { state: AppState }
>('recommendations/lookupUrl', async ({ url }, { getState }) => {
  const token = getState().user.authToken;
  return sendGetRequest(`/api/recommendations/lookup-url/?url=${encodeURIComponent(url)}`, token);
});
